import { Component, OnInit } from '@angular/core';
import { KeywordsSelected } from '@models/keywordsSelected.model';
import { StudySearchStrategy } from '@models/searchStrategy.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogService } from '@services/dialog.service';
import { InternetReviewService } from '@services/internet-review.service';
import { KeywordExtractionService } from '@services/keyword-extraction.service';
import { LoggingService } from '@services/logging.service';
import { SearchStrategyService } from '@services/search-strategy.service';
import { AcknowledgeMessage } from '@shared/_constants';
import { SelectItem } from 'primeng/api';
import { PageTitlesRetrievalSelectionService } from '../page-titles-retrieval-selection.service';
import { InternetReviewSelectionService } from '../internet-review-selection.service';
import { Store } from '@ngrx/store';
import { KeywordType } from 'src/app/mystudies/_study_constants';
import { scrapingType } from '../../../_study_constants';

@Component({
  selector: 'app-internet-review-scrape-dialog',
  templateUrl: './internet-review-scrape-dialog.component.html',
  styleUrls: ['./internet-review-scrape-dialog.component.css'],
  providers: [SearchStrategyService, KeywordExtractionService],
})
export class InternetReviewScrapeDialogComponent implements OnInit {
  public readOnly = false;
  public studyId: number;

  showLoader = false;
  searchStrategy: StudySearchStrategy;
  keywordsInclude: string;
  keywordsExclude: string;
  productIncludeListOptions: SelectItem[];
  functionIncludeListOptions: SelectItem[];
  excludeListOptions: SelectItem[];
  selectedProductList: SelectItem[] = [];
  selectedFunctionList: SelectItem[] = [];
  selectedExcludeList: SelectItem[] = [];
  selectedKeywords: KeywordsSelected[] = [];
  depthToScrape: number = 2;
  numberOfPages: number = 25;
  numberOfPagesInvalid: boolean = false;
  depthToScrapeInvalid: boolean = false;
  limitScraping: { value: scrapingType; label: string; checked: boolean }[] = [
    { value: scrapingType.Contact, label: 'Contact/Location', checked: false },
    { value: scrapingType.MainPage, label: 'Main Page', checked: false },
    { value: scrapingType.AboutUs, label: 'About Us', checked: false },
    { value: scrapingType.Products, label: 'Products', checked: false },
    { value: scrapingType.Service, label: 'Service', checked: false },
  ];
  includeNativeLanguage: boolean;
  comparableId: null;

  constructor(
    private activeModal: NgbActiveModal,
    private searchStrategyService: SearchStrategyService,
    private keywordExtractionService: KeywordExtractionService,
    private logger: LoggingService,
    private internetReviewService: InternetReviewService,
    private dialogService: DialogService,
    private pageTitlesRetrievalSelectionService: PageTitlesRetrievalSelectionService,
    private internetReviewSelectionService: InternetReviewSelectionService,
    private store: Store<any>,
  ) {}

  async ngOnInit() {
    try {
      this.logger.info(this.constructor.name, 'ngOnInit', 'Start');
      this.showLoader = true;
      this.searchStrategy = this.searchStrategyService.studySearchStrategy;
      this.selectedKeywords = await this.keywordExtractionService.getSelectedKeywords();
      this.convertKeywordsToSelectItems();
    } finally {
      this.showLoader = false;
      this.logger.info(this.constructor.name, 'ngOnInit', 'End');
    }
  }

  async onRunScrapeClick(): Promise<void> {
    try {
      this.showLoader = true;

      // include words
      let includeKeywords = '';
      let excludeKeywords = '';

      if (this.keywordsInclude && this.keywordsInclude.length > 0) {
        includeKeywords = this.keywordsInclude;
      }
      if (this.selectedFunctionList && this.selectedFunctionList.length > 0) {
        includeKeywords += (includeKeywords.length > 0 ? ';' : '') + this.selectedFunctionList.map((s) => s['name']).join(';');
      }
      if (this.selectedProductList && this.selectedProductList.length > 0) {
        includeKeywords += (includeKeywords.length > 0 ? ';' : '') + this.selectedProductList.map((s) => s['name']).join(';');
      }
      if (this.keywordsExclude && this.keywordsExclude.length > 0) {
        excludeKeywords = this.keywordsExclude;
      }
      if (this.selectedExcludeList && this.selectedExcludeList.length > 0) {
        excludeKeywords += (excludeKeywords.length > 0 ? ';' : '') + this.selectedExcludeList.map((s) => s['name']).join(';');
      }

      // set to null if no keywords
      includeKeywords = includeKeywords.length > 0 ? includeKeywords : null;
      excludeKeywords = excludeKeywords.length > 0 ? excludeKeywords : null;
      const depthToScrape = this.depthToScrape;
      const numberOfPages = this.numberOfPages;
      let limitScrapingFlags = 0;
      this.limitScraping.forEach((element) => {
        if (element.checked) {
          limitScrapingFlags = limitScrapingFlags | element.value;
        }
      });

      // run this asynchronously, without "await"
      this.internetReviewService
        .executeScrape(
          this.searchStrategy.studySearchStrategyId,
          includeKeywords,
          excludeKeywords,
          this.comparableId,
          limitScrapingFlags,
          this.depthToScrape,
          this.numberOfPages,
          this.includeNativeLanguage,
        )
        .subscribe();

      const selectedItems = this.internetReviewSelectionService.getSelected();
      this.pageTitlesRetrievalSelectionService.addSelectedItemsForPageTitlesRetrieval(this.studyId, selectedItems);

      await this.dialogService.acknowledge('Scrape', AcknowledgeMessage.ScrapeStarted, 'OK');

      this.activeModal.close();
    } finally {
      this.showLoader = false;
    }
  }

  onClearAll(): void {
    this.keywordsInclude = '';
    this.keywordsExclude = '';
    this.selectedProductList = [];
    this.selectedFunctionList = [];
    this.selectedExcludeList = [];
    this.limitScraping.forEach((item) => {
      item.checked = false;
    });
    this.depthToScrape = 2;
    this.numberOfPages = 25;
    this.numberOfPagesInvalid = false;
    this.depthToScrapeInvalid = false;
  }

  close(): void {
    this.logger.info(this.constructor.name, 'close', 'Start');
    this.activeModal.close();
  }

  private convertKeywordsToSelectItems() {
    this.logger.info(this.constructor.name, 'convertKeywordsToSelectItems', 'Start');
    try {
      this.productIncludeListOptions = this.convertKeywordListToSelectList(
        this.selectedKeywords.filter((k) => k.keywordTypeId === KeywordType.IncludeProduct && k.isActive),
      );
      this.functionIncludeListOptions = this.convertKeywordListToSelectList(
        this.selectedKeywords.filter((k) => k.keywordTypeId === KeywordType.IncludeFunction && k.isActive),
      );
      this.excludeListOptions = this.convertKeywordListToSelectList(
        this.selectedKeywords.filter((k) => k.keywordTypeId === KeywordType.Exclude && k.isActive),
      );
    } finally {
      this.logger.info(this.constructor.name, 'convertKeywordsToSelectItems', 'End');
    }
  }

  onNumberOfPagesChange() {
    const numberOfPagesValue = this.numberOfPages;
    if (numberOfPagesValue <= 0 || isNaN(numberOfPagesValue)) {
      this.numberOfPagesInvalid = true;
    } else {
      this.numberOfPagesInvalid = false;
    }
  }

  onDepthToScrapeChange() {
    const depthToScrapeValue = this.depthToScrape;
    if (depthToScrapeValue <= 0 || isNaN(depthToScrapeValue)) {
      this.depthToScrapeInvalid = true;
    } else {
      this.depthToScrapeInvalid = false;
    }
  }

  private convertKeywordListToSelectList(keywordList: KeywordsSelected[]): SelectItem[] {
    this.logger.info(this.constructor.name, 'convertKeywordListToSelectList', 'Start');
    const selectItemList: SelectItem[] = [];
    try {
      keywordList.forEach((k) => {
        const selectItem: SelectItem = {
          label: k.keyword,
          value: {
            id: k.keywordsSelectedId,
            name: k.keyword,
          },
        };
        selectItemList.push(selectItem);
      });
      return selectItemList;
    } finally {
      this.logger.info(this.constructor.name, 'convertKeywordListToSelectList', 'End');
    }
  }
}
